import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, Loader2, MessageSquare, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

import { useTranslation } from 'react-i18next';

import { homeTarget, ShareHomeButton } from '@/components/ShareHomeButton';
/**
 * SharedVersePage — public landing page for a shared Bible verse.
 *
 * Renders the verse beautifully, then offers two clear conversion CTAs:
 *   1. "Open in Eili"  — deep-links into the Bible reader at the
 *      shared reference (works for both signed-in users and guests).
 *   2. "Discuss with Eili" — drops the visitor into a guest chat
 *      seeded with the verse so they immediately try the product.
 */
export default function SharedVersePage() {
  const {
    t
  } = useTranslation();

  const { shareId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${window.location.origin}/api/bible/share/${shareId}`);
        if (!cancelled) setDoc(res.data);
      } catch {
        if (!cancelled) setError('This verse share is no longer available.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  if (loading) {
    return (
      <div
        className="min-h-dvh flex items-center justify-center"
        style={{ background: '#F5F0E8' }}
      >
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#8B6F47' }} />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div
        className="min-h-dvh flex flex-col items-center justify-center gap-4 px-6"
        style={{ background: '#F5F0E8', color: '#2C2416' }}
      >
        <BookOpen className="h-12 w-12" style={{ color: '#8B6F47', opacity: 0.5 }} />
        <p className="text-base text-center">{error || 'Verse not found.'}</p>
        <Button
          onClick={() => navigate(homeTarget(), { replace: true })}
          className="rounded-xl px-5 py-2.5 text-sm font-medium"
          style={{ background: '#8B6F47', color: '#fff' }}
        >
          {t('generated.sharedversepage_explore_eili_10a7')}
        </Button>
      </div>
    );
  }

  const multi = doc.verses.length > 1;
  const handleOpenInReader = () => {
    // Drop into the Bible reader with the shared reference. The reader
    // already accepts string references via `state.targetReference`.
    navigate('/bible', { state: { targetReference: doc.reference } });
  };

  const handleDiscussWithEili = () => {
    // Seed a chat session with the verse as the opening user prompt.
    // ChatPage reads `state.seedMessage` on mount.
    const verseText = doc.verses.map((v) => v.text).join(' ');
    const seed = `I'd like to discuss ${doc.reference} (${doc.translation.toUpperCase()}):\n\n"${verseText}"`;
    navigate('/chat', { state: { seedMessage: seed } });
  };

  return (
    <div
      className="min-h-dvh flex flex-col"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F5F0E8 100%)',
        color: '#2C2416',
        // Allow long passages (multi-verse shares, footers) to scroll
        // naturally when the content overflows the viewport.
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
      data-testid="shared-verse-page"
    >
      <ShareHomeButton />
      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-xl mx-auto">
          {/* Brand */}
          <div className="flex items-center justify-center gap-2 mb-10">
            <img src="/logo.png" alt={t('generated.sharedversepage_eili_b140')} className="h-7 w-7 object-contain" />
            <span
              className="text-lg font-bold"
              style={{ fontFamily: "'Playwrite ES Deco', cursive", color: '#8B6F47' }}
            >
              {t('generated.sharedversepage_eili_b140')}
            </span>
            <span className="text-[11px] ml-1 uppercase tracking-widest" style={{ color: '#6B5E4E' }}>
              {t('generated.sharedversepage_scripture_5a83')}
            </span>
          </div>

          {/* Verse card */}
          <div
            className="relative rounded-3xl overflow-hidden p-8 md:p-12 mb-6"
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2D9C7',
              boxShadow: '0 20px 50px rgba(139, 111, 71, 0.10)',
            }}
            data-testid="shared-verse-card"
          >
            {/* Decorative quote mark */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: 180,
                color: '#8B6F47',
                opacity: 0.06,
                fontWeight: 700,
                lineHeight: 1,
                pointerEvents: 'none',
                fontFamily: 'Georgia, serif',
              }}
            >
              “
            </div>

            <p
              className="relative text-center leading-relaxed"
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: doc.verses.length > 3 ? '1.05rem' : '1.25rem',
                lineHeight: 1.7,
                color: '#2C2416',
              }}
            >
              {doc.verses.map((v, i) => (
                <span key={i}>
                  {multi && (
                    <sup
                      style={{
                        color: '#8B6F47',
                        fontWeight: 700,
                        marginRight: 4,
                        fontSize: '0.6em',
                      }}
                    >
                      {v.verse}
                    </sup>
                  )}
                  {v.text}{' '}
                </span>
              ))}
            </p>

            <div
              className="mt-8 pt-6 text-center"
              style={{ borderTop: '1px solid #E2D9C7' }}
            >
              <p
                className="text-sm font-semibold tracking-widest"
                style={{ color: '#8B6F47' }}
              >
                {doc.reference.toUpperCase()}
              </p>
              <p
                className="text-[11px] uppercase tracking-widest mt-1"
                style={{ color: '#6B5E4E' }}
              >
                {doc.translation.toUpperCase()}
              </p>
            </div>
          </div>

          {/* Dual CTA */}
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            <Button
              onClick={handleOpenInReader}
              className="rounded-2xl py-6 font-medium gap-2 text-base"
              style={{ background: '#8B6F47', color: '#fff' }}
              data-testid="shared-verse-open-reader-btn"
            >
              <BookOpen className="h-5 w-5" />
              {t('generated.sharedversepage_open_in_eili_2edb')}
            </Button>
            <Button
              onClick={handleDiscussWithEili}
              variant="outline"
              className="rounded-2xl py-6 font-medium gap-2 text-base"
              style={{
                borderColor: '#8B6F47',
                color: '#8B6F47',
                background: 'transparent',
              }}
              data-testid="shared-verse-discuss-btn"
            >
              <MessageSquare className="h-5 w-5" />
              {t('generated.sharedversepage_discuss_with_eili_355a')}
            </Button>
          </div>

          {/* Get-the-app footer */}
          <div
            className="rounded-2xl px-5 py-4 text-center text-sm"
            style={{ background: '#FFFFFF', border: '1px solid #E2D9C7', color: '#6B5E4E' }}
          >
            <p className="mb-2">
              {t('generated.sharedversepage_explore_commentary_parallel_7124')}
            </p>
            <button
              onClick={() => navigate(homeTarget(), { replace: true })}
              className="inline-flex items-center gap-1.5 font-medium"
              style={{ color: '#8B6F47' }}
              data-testid="shared-verse-get-app-btn"
            >
              {t('generated.sharedversepage_get_eili_5782')}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
